import {
  Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, min } from 'rxjs';
import { VerificationComponent } from 'src/app/components/verification/verification.component';
import { AuthService } from 'src/app/services/auth.service';
import { CoursesService } from 'src/app/services/courses.service';
import { ToastService } from 'src/app/services/toast.service';
import { formatDate as formatDateUtil } from 'src/app/utils/dates.utils';
import Course, { getSubmittableCourseRequirements, hasBlockedSchoolCounselorRequirementSelection, SCHOOL_COUNSELOR_REQUIREMENT_ERROR_TEXT } from '../../course.class';
import { UploadFileComponent, PendingFileInfo } from 'src/app/components/upload-file/upload-file.component';
import { CourseRequestAttachmentsComponent } from 'src/app/routes/profile/components/my-course-requests/components/update-my-course-request/components/course-request-attachments/course-request-attachments.component';
import { CatalogSuggestionAttachmentsComponent } from 'src/app/routes/profile/components/my-catalog-suggestions/components/update-my-catalog-suggestion/components/catalog-suggestion-attachments/catalog-suggestion-attachments.component';

@Component({
  selector: 'app-finish',
  templateUrl: './finish.component.html',
  styleUrls: ['./finish.component.scss'],
})
export class FinishComponent implements OnInit, OnDestroy {
  @Output() updateCourseObject = new EventEmitter<object>();
  @Output() stepBack = new EventEmitter<any>();
  @Output() closeDialogEmitter = new EventEmitter<any>();
  @Output() completeCourseSessionDetails = new EventEmitter<any>();
  @Output() isLoadingStatus = new EventEmitter<boolean>(); // this is used to disable the admin button in the details page until the session data is loaded
  @Output() courseCreated = new EventEmitter<any>(); // Emit course creation response for parent to handle file uploads
  @Input() isCourseDetails = false;
  @Input() course: Course;
  @Input() courseItemId: number;
  @Input() Id: number;
  @Input() isEditMode: boolean;
  @Input() pendingDocuments: PendingFileInfo[] = [];
  @Input() attachmentsStep?: CourseRequestAttachmentsComponent; // Reference to attachments step from parent
  @Input() catalogSuggestionAttachmentsStep?: CatalogSuggestionAttachmentsComponent; // Reference to catalog suggestion attachments step from parent

  @ViewChild(UploadFileComponent) uploadFileComponent!: UploadFileComponent;
  coursesSubscription: Subscription;
  courseSessionDataSubscription: Subscription;
  error: string;
  isLoading = false;
  courseDataSubscription: Subscription;
  permissions: any;
  permissionsSubscription: Subscription;
  fileTypeInS3 = 'COURSE_DOCUMENT';

  constructor(
    public dialog: MatDialog,
    private coursesService: CoursesService,
    private authService: AuthService,
    private toastService: ToastService,
  ) { }

  ngOnInit(): void {
    // here we get the users permissions
    this.permissionsSubscription = this.authService.permissions$.subscribe((permissions: any) => {
      this.permissions = permissions;
    })
  }

  get displayRequirementNames(): string[] {
    const list = this.course?.requirementsList ?? [];
    const names: string[] = [];
    const seenNames = new Set<string>();
    const seenIds = new Set<number>();

    list.forEach((r: any) => {
      const id = Number(r?.requirementId ?? r?.Id ?? r?.id ?? r?.Requirement?.id);
      if (Number.isFinite(id) && seenIds.has(id)) return;
      if (Number.isFinite(id)) seenIds.add(id);

      const nodeType = r?.nodeType;
      const parentName = (r?.topMostParentName || '').trim();
      const requirementName = (
        r?.requirement || r?.Requirement?.requirement || ''
      ).trim();

      const showParentHeading =
        parentName &&
        (nodeType === 'GROUP_LEAF' || nodeType === 'GROUP_NODE') &&
        !seenNames.has(parentName);
      if (showParentHeading) {
        seenNames.add(parentName);
        names.push(parentName);
      }

      if (requirementName && !seenNames.has(requirementName)) {
        seenNames.add(requirementName);
        names.push(requirementName);
      }
    });

    return names;
  }
  
  isUrl(url: string) {
    const isVirtual = this.course.virtualFlag === true;
    const isUrl = url.startsWith('http') || url.startsWith('https') || url.startsWith('www.');
    return isVirtual && isUrl;
  }

  shortenUrl(url: string) {
    if (!url) return '-';

    const isTooLong = url.length > 50;

    if (!isTooLong) return url;

    const first20 = url.substring(0, 20);
    const last10 = url.substring(url.length - 10);

    return `${first20}...${last10}`;
  }

  formatAsUrl(url: string) {
    const startsWithHttp = url.startsWith('http') || url.startsWith('https');
    const startsWithWww = url.startsWith('www.');

    if (startsWithHttp) return url;

    if (startsWithWww) {
      return `https://${url}`;
    }

    return url;
  }

  updateLocalCourseParameter = (event: any, parameter: string) => {
    // this.course[parameter] = event;
  }

  populateDate(date: string | Date | undefined) {
    if (!date) return '-';
    try {
      return formatDateUtil.asDateLocal(date) || '-';
    } catch {
      return '-';
    }
  }

  populateTime(time: string | Date | undefined) {
    if (!time) return '';
    try {
      const timezone = this.course?.localTimezone;
      const formatted = formatDateUtil.asTimeWithTimezone(time, timezone);
      if (formatted && formatted !== 'Invalid Date') return formatted;
    } catch {
      // Fall through to HH:mm fallback
    }
    // Fallback for HH:mm string format (e.g. "09:00" from form input) or when formatDateUtil throws
    const str = typeof time === 'string' ? time : '';
    const [hours, minutes] = str.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return '';
    const hour12 = hours % 12 || 12;
    const amPm = hours >= 12 ? 'PM' : 'AM';
    const m = minutes < 10 ? `0${minutes}` : minutes;
    return `${hour12}:${m} ${amPm}`;
  }

  saveCourse = () => {
    // final validation check before sending request
    const missingProps: string[] = [];
    if (this.course.courseName === '') {
      missingProps.push('Course Name');
    }
    if (this.course.trackingValue === '') {
      missingProps.push('Tracking Value');
    }
    if (this.course.courseType === '') {
      missingProps.push('Course Type');
    }
    if (!this.course.courseSize) {
      missingProps.push('Course Size');
    }
    if (this.course.courseDescription === '') {
      missingProps.push('Course Description');
    }
    if (this.course.location === '') {
      missingProps.push('Location');
    }

    if (this.course.isAnytime) {
      if (!this.course.dateOffered) {
        missingProps.push('Anytime - Date Offered');
      }
    } else if (!this.course.isAsync) {
      if (!this.course.dateOffered) {
        missingProps.push('Norm - Date Offered');
      }
      if (!this.course.sessionStartDate) {
        missingProps.push('Norm - Session Start Date');
      }
      if (!this.course.sessionEndDate) {
        missingProps.push('Norm - Session End Date');
      }
    }

    if (!this.course.regStartDate) {
      missingProps.push('Registration Start Date');
    }
    if (!this.course.regEndDate) {
      missingProps.push('Registration End Date');
    }
    if (getSubmittableCourseRequirements(this.course?.requirementsList ?? []).length === 0) {
      missingProps.push('Requirements');
    }
    if (hasBlockedSchoolCounselorRequirementSelection(this.course?.requirementsList ?? [])) {
      this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Invalid requirement selection',
          text: SCHOOL_COUNSELOR_REQUIREMENT_ERROR_TEXT,
        },
      });
      return;
    }
    // if (!this.course?.approvalId) {
    //   missingProps.push('Approval Level');
    // }

    // if (missingProps.length > 0) {
    //   this.error = `Please go back and fill out all required fields: ${missingProps.join(', ')}`;
    //   return
    // }

    // here we do a final check on the users permissions to see if they are allowed to create a course
    if (this.permissions?.COURSE_ADD === 0) {
      this.dialog.closeAll();
      this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert', title: 'Oops, something went wrong', text: 'You do not have permission to create a course',
        },
      })
      return
    }

    // this.isLoading = true;
    const createCourse = this.course.buildCreateRequest();
    this.coursesSubscription = this.coursesService.addCourse(createCourse).subscribe({
      next: async (response: any) => {
        try {
          // Check if the request was successful (200 response)
          if (response?.statusCode === 1000 || response?.data) {
            // Extract courseItemId from response (similar to course requests)
            let courseItemId: number | null = null;
            let courseId: number | null = null;

            // Try to extract from response data
            if (response?.data) {
              const courseData = response.data;
              
              // Check response.data.items[0].id (new structure from addCourse)
              if (courseData?.items && Array.isArray(courseData.items) && courseData.items.length > 0) {
                const item = courseData.items[0];
                courseItemId = Number(item.id);
                courseId = Number(item.courseId || courseData.course?.id || courseData.courseId);
              }
              
              // Check response.data.course.CourseItems (nested structure)
              if ((!courseItemId || isNaN(courseItemId)) && courseData?.course?.CourseItems) {
                if (Array.isArray(courseData.course.CourseItems) && courseData.course.CourseItems.length > 0) {
                  const item = courseData.course.CourseItems[0];
                  courseItemId = Number(item.id);
                  courseId = Number(item.courseId);
                } else if (typeof courseData.course.CourseItems === 'object') {
                  courseItemId = Number(courseData.course.CourseItems.id);
                  courseId = Number(courseData.course.CourseItems.courseId);
                }
              }
              
              // Check response.data.CourseItems (direct structure)
              if ((!courseItemId || isNaN(courseItemId)) && courseData?.CourseItems) {
                if (Array.isArray(courseData.CourseItems) && courseData.CourseItems.length > 0) {
                  const item = courseData.CourseItems[0];
                  courseItemId = Number(item.id);
                  courseId = Number(item.courseId || courseData.id);
                } else if (typeof courseData.CourseItems === 'object') {
                  courseItemId = Number(courseData.CourseItems.id);
                  courseId = Number(courseData.CourseItems.courseId || courseData.id);
                }
              }
              
              // Check for direct courseItemId property
              if ((!courseItemId || isNaN(courseItemId)) && courseData?.courseItemId) {
                courseItemId = Number(courseData.courseItemId);
                courseId = Number(courseData.courseId || courseData.id);
              }
              
              // Check response.data.course.id (might be courseId, not courseItemId)
              if ((!courseId || isNaN(courseId)) && courseData?.course?.id) {
                courseId = Number(courseData.course.id);
              }
              
              // Check for id only (might be courseId)
              if ((!courseItemId || isNaN(courseItemId)) && courseData?.id) {
                courseId = Number(courseData.id);
              }
            }

            // Check if there are pending documents to upload
            const pendingDocs = this.attachmentsStep?.getPendingDocuments?.() || [];
            const hasPendingDocuments = pendingDocs.length > 0;

            if (hasPendingDocuments && courseItemId && !isNaN(courseItemId) && courseItemId > 0) {
              // Has documents to upload and valid courseItemId - upload in second call (S3 upload)
              try {
                // Set course IDs on the attachments step component
                this.attachmentsStep?.setCourseIdsFromData?.(response.data?.course || response.data || response);
                
                // Wait a moment for the component to update
                await new Promise(resolve => setTimeout(resolve, 200));
                
                // Verify files are still available before uploading
                const filesBeforeUpload = this.attachmentsStep?.getPendingDocuments?.() || [];
                
                if (filesBeforeUpload.length === 0) {
                  this.toastService.setToast({
                    text: 'Course created successfully, but no files were found to upload.',
                    type: 'warn',
                    icon: true,
                  });
                  this.courseCreated.emit(response);
                  return;
                }
                
                // Trigger S3 upload - this will:
                // 1. Upload files to S3 via generateSignedUrl and uploadSignedUrl
                // 2. Save document metadata via uploadCourseDocuments
                await this.attachmentsStep?.uploadFilesForCourse?.(courseItemId);
              } catch (err) {
                this.toastService.setToast({
                  text: 'Course created successfully, but document upload failed. Please upload documents manually.',
                  type: 'warn',
                  icon: true,
                });
              } finally {
                this.toastService.setToast({
                  text: 'Course successfully added', type: 'success', icon: true,
                });
                this.courseCreated.emit(response);
              }
            } else {
              // No documents to upload or no valid courseItemId
              this.toastService.setToast({
                text: 'Course successfully added', type: 'success', icon: true,
              });
              this.courseCreated.emit(response);
            }
          } else {
            // Request was not successful
            const errorMessage = response?.message || 'The course could not be created. Please try again.';
            this.dialog.open(VerificationComponent, {
              data: {
                type: 'alert',
                title: 'Oops, something went wrong',
                text: errorMessage,
              },
            });
          }
        } catch (err) {
          // Catch any unexpected errors during response processing
          this.dialog.open(VerificationComponent, {
            data: {
              type: 'alert',
              title: 'Oops, something went wrong',
              text: 'An unexpected error occurred while processing the course creation. Please try again.',
            },
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        let message;
        if (error.error.statusCode === 4003) {
          message = 'Course name already exists, please choose another name'
        }
        this.dialog.open(VerificationComponent, {
          data: {
            type: 'alert', title: 'Oops, something went wrong', text: message || error.error.errMsg || error.error.message,
          },
        })
      },
    })

  }

  reloadDocuments = () => {
    if (this.uploadFileComponent) {
      this.uploadFileComponent.fetchExistingDocuments().subscribe();
    }
  }

  onDocumentDeleted = () => {
    // When a document is deleted, reload the documents list
    this.reloadDocuments();
  }

  stepBackward = () => {
    this.stepBack.emit();
  }

  formatDate = (date: any) => {
    return date;
  }

  closeDialog = () => {
    this.closeDialogEmitter.emit();
  }

  ngOnDestroy(): void {
    this.coursesSubscription?.unsubscribe();
    this.courseSessionDataSubscription?.unsubscribe();
    this.courseDataSubscription?.unsubscribe();
  }

}
